/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Light boot seam for the voice bridge. `window.bullfrog.playSearch` must
// exist synchronously at app boot (the iOS Siri shell calls it as soon as
// the WebView claims ready), but the real implementation drags
// searchService + cloudQueue + RecommendationRenderer — none of which
// belong in the app-shell chunk. Install a queueing stub here and swap in
// the real bridge on first use; `installPlaySearchBridge` drains
// `__pendingPlaySearch` on install so no query is lost.

import { asError } from '@/util/errorUtil'
import { ctx } from '@/util/context'
import type { PlaySearchDeps } from '@/voice/playSearch'

interface VoiceWindow {
    bullfrog?: {
        playSearch?: (query: string) => void
        __pendingPlaySearch?: string[]
    }
}

/**
 * Install a synchronous `window.bullfrog.playSearch` stub that queues
 * queries and lazily loads the real bridge (which replaces the stub and
 * drains the queue). Also kicks the lazy load immediately when the shell
 * queued queries before the SPA booted.
 */
export function installPlaySearchBridgeLazy(deps: PlaySearchDeps): void {
    const w = globalThis as VoiceWindow
    w.bullfrog ??= {}
    let loading = false
    const loadRealBridge = () => {
        if (loading) return
        loading = true
        import('@/voice/playSearch')
            .then((m) => m.installPlaySearchBridge(deps))
            .catch((err: unknown) => {
                loading = false
                ctx('voice').errorHandler(asError(err))
            })
    }
    w.bullfrog.playSearch = (query: string) => {
        ;(w.bullfrog!.__pendingPlaySearch ??= []).push(query)
        loadRealBridge()
    }
    // Shell got ahead of us — queries queued before boot; load now.
    if ((w.bullfrog.__pendingPlaySearch?.length ?? 0) > 0) loadRealBridge()
}
